'use client';

import React, { useState } from 'react';
import { Package, TrendingUp, AlertTriangle, ArrowRight, RefreshCw } from 'lucide-react';
import { MOCK_EGG_INVENTORY, MOCK_TRANSFERS, MOCK_DAMAGE_RECORDS, EggTransfer, EggDamageRecord } from '../../components/eggProductionData';
import Icon from '@/components/ui/AppIcon';
import { useRole } from '@/lib/useRole';


function StockCard({ item }: { item: typeof MOCK_EGG_INVENTORY[0] }) {
  const pct = item.closingStock > 0 ? Math.round((item.closingStock / (item.openingStock + item.produced)) * 100) : 0;
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-xs font-bold text-gray-900">{item.eggType}</p>
          <p className="text-xs text-gray-400">{item.grade}</p>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
          item.status === 'available' ? 'bg-green-100 text-green-700' :
          item.status === 'reserved' ? 'bg-blue-100 text-blue-700' :
          item.status === 'expired'? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
        }`}>{item.status.charAt(0).toUpperCase() + item.status.slice(1)}</span>
      </div>
      <div className="flex items-end justify-between mb-2">
        <div>
          <p className="text-2xl font-bold text-gray-900">{item.closingStock.toLocaleString()}</p>
          <p className="text-xs text-gray-400">eggs in stock</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold text-gray-700">{item.trays}</p>
          <p className="text-xs text-gray-400">trays</p>
        </div>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-1.5 mb-3">
        <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-green-50 rounded-lg p-1.5">
          <p className="text-xs font-bold text-green-700">{item.produced.toLocaleString()}</p>
          <p className="text-xs text-gray-400">Produced</p>
        </div>
        <div className="bg-red-50 rounded-lg p-1.5">
          <p className="text-xs font-bold text-red-600">{item.sold.toLocaleString()}</p>
          <p className="text-xs text-gray-400">Sold</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-1.5">
          <p className="text-xs font-bold text-amber-600">{item.damaged}</p>
          <p className="text-xs text-gray-400">Damaged</p>
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-2">Expires: {item.expiryDate} · {item.store}</p>
    </div>
  );
}

const TABS = ['Stock Overview', 'Transfers', 'Damages'];

export default function EggInventoryContent() {
  const [activeTab, setActiveTab] = useState('Stock Overview');
  const [toast, setToast] = useState<string | null>(null);
  const { can } = useRole();

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const transferStatusBadge = (status: EggTransfer['status']) => {
    const map = {
      draft: 'bg-gray-100 text-gray-600',
      pending: 'bg-amber-100 text-amber-700',
      in_transit: 'bg-blue-100 text-blue-700',
      received: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-600',
    };
    const labels = { draft: 'Draft', pending: 'Pending', in_transit: 'In Transit', received: 'Received', cancelled: 'Cancelled' };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${map[status]}`}>{labels[status]}</span>;
  };

  const damageStatusBadge = (status: EggDamageRecord['status']) => {
    const map = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-600' };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${map[status]}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
  };

  const totalStock = MOCK_EGG_INVENTORY.reduce((s, i) => s + i.closingStock, 0);
  const totalTrays = MOCK_EGG_INVENTORY.reduce((s, i) => s + i.trays, 0);
  const totalDamaged = MOCK_EGG_INVENTORY.reduce((s, i) => s + i.damaged, 0);
  const totalSold = MOCK_EGG_INVENTORY.reduce((s, i) => s + i.sold, 0);

  return (
    <div className="p-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm">
          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />{toast}
        </div>
      )}

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Egg Inventory</h1>
          <p className="text-sm text-gray-500 mt-1">Track egg stock levels, movements, transfers, and losses across all stores.</p>
        </div>
        {can('egg_inventory.adjust') && (
          <button onClick={() => showToast('Stock adjustment form coming soon.')} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-sm">
            <RefreshCw size={15} />Adjust Stock
          </button>
        )}
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Egg Stock', value: totalStock.toLocaleString(), sub: `${totalTrays} trays`, icon: Package, bg: 'bg-green-100', color: 'text-green-600' },
          { label: 'Total Sold', value: totalSold.toLocaleString(), sub: 'All time', icon: TrendingUp, bg: 'bg-blue-100', color: 'text-blue-600' },
          { label: 'Total Damaged', value: totalDamaged, sub: 'Recorded losses', icon: AlertTriangle, bg: 'bg-red-100', color: 'text-red-500' },
          { label: 'Active Transfers', value: MOCK_TRANSFERS.filter(t => t.status === 'in_transit').length, sub: 'In transit', icon: ArrowRight, bg: 'bg-amber-100', color: 'text-amber-600' },
        ].map(c => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${c.bg}`}>
                <Icon size={18} className={c.color} />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">{c.label}</p>
                <p className="text-xl font-bold text-gray-900 mt-0.5">{c.value}</p>
                <p className="text-xs text-gray-400">{c.sub}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${activeTab === tab ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Stock Overview */}
      {activeTab === 'Stock Overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MOCK_EGG_INVENTORY.map(item => <StockCard key={item.id} item={item} />)}
          </div>

          {/* Movement Table */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">Stock Ledger</h2>
              <p className="text-xs text-gray-400 mt-0.5">Opening → Produced → Sold → Damaged → Closing</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Egg Type', 'Grade', 'Store', 'Opening', 'Produced', 'Transferred In', 'Transferred Out', 'Sold', 'Damaged', 'Closing Stock'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MOCK_EGG_INVENTORY.map((item, i) => (
                    <tr key={item.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                      <td className="px-4 py-3 text-xs font-semibold text-gray-900">{item.eggType}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{item.grade}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{item.store}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{item.openingStock.toLocaleString()}</td>
                      <td className="px-4 py-3 text-xs text-green-600 font-medium">+{item.produced.toLocaleString()}</td>
                      <td className="px-4 py-3 text-xs text-blue-600">+{item.transferredIn}</td>
                      <td className="px-4 py-3 text-xs text-amber-600">-{item.transferredOut.toLocaleString()}</td>
                      <td className="px-4 py-3 text-xs text-red-500">-{item.sold.toLocaleString()}</td>
                      <td className="px-4 py-3 text-xs text-red-400">-{item.damaged}</td>
                      <td className="px-4 py-3 text-xs font-bold text-gray-900">{item.closingStock.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Transfers */}
      {activeTab === 'Transfers' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-base font-bold text-gray-900">Egg Transfers</h2>
            {can('egg_transfers.manage') && (
              <button onClick={() => showToast('Transfer form coming soon.')} className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700">
                <ArrowRight size={13} />New Transfer
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Transfer #', 'Date', 'From', 'To', 'Grade', 'Qty', 'Trays', 'Transferred By', 'Status'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MOCK_TRANSFERS.map((t, i) => (
                  <tr key={t.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                    <td className="px-4 py-3 text-xs font-semibold text-green-700">{t.transferNumber}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{t.date}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      <div>{t.sourceFarm}</div>
                      <div className="text-gray-400">{t.sourceStore}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      <div>{t.destinationFarm}</div>
                      <div className="text-gray-400">{t.destinationStore}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{t.eggGrade}</td>
                    <td className="px-4 py-3 text-xs font-semibold text-gray-900">{t.quantity.toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{t.trays}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{t.transferredBy}</td>
                    <td className="px-4 py-3">{transferStatusBadge(t.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Damages */}
      {activeTab === 'Damages' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-base font-bold text-gray-900">Egg Damage & Losses</h2>
            {can('egg_damages.manage') && (
              <button onClick={() => showToast('Damage form coming soon.')} className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700">
                <AlertTriangle size={13} />Record Damage
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Damage #', 'Date', 'Farm', 'Store', 'Egg Type', 'Qty', 'Reason', 'Reported By', 'Approved By', 'Status'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MOCK_DAMAGE_RECORDS.map((d, i) => (
                  <tr key={d.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                    <td className="px-4 py-3 text-xs font-semibold text-red-700">{d.damageNumber}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{d.date}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{d.farm}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{d.store}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{d.eggType}</td>
                    <td className="px-4 py-3 text-xs font-semibold text-red-600">{d.quantity}</td>
                    <td className="px-4 py-3 text-xs text-gray-600 capitalize">{d.reason.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{d.reportedBy}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{d.approvedBy ?? '—'}</td>
                    <td className="px-4 py-3">{damageStatusBadge(d.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
